/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { Button, Modal } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import '@/quote-management/components/ConfirmationModal/ConfirmationModal.css';

export interface ConfirmationModalProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  open?: boolean;
  title?: VNode | string;
  message?: VNode | string;
  cancelLabel?: VNode | string;
  confirmLabel?: VNode | string;
  onCancel?: () => void;
  onConfirm?: () => void;
  onClose?: () => void;
  showCloseButton?: boolean;
  confirmationBanner?: VNode | null;
}

export const ConfirmationModal: FunctionComponent<ConfirmationModalProps> = ({
  open = false,
  title,
  message,
  cancelLabel,
  confirmLabel,
  onCancel,
  onConfirm,
  onClose,
  showCloseButton,
  confirmationBanner,
  ...props
}) => {
  const dictionary = useText({
    cancel: 'ConfirmationModal.cancel',
    confirm: 'ConfirmationModal.confirm',
  });

  if (!open) {
    return null;
  }

  const { className, ...restProps } = props;

  return (
    <Modal
      {...restProps}
      size={'medium'}
      onClose={onClose || onCancel}
      clickToDismiss={true}
      escapeToDismiss={true}
      showCloseButton={showCloseButton}
      data-testid="confirmation-modal"
      className={className}
    >
      <div className="confirmation-modal__content">
        {confirmationBanner && (
          <div
            className="confirmation-modal__banner"
            data-testid="confirmation-modal-banner"
          >
            {confirmationBanner}
          </div>
        )}
        {title && (
          <div
            className="confirmation-modal__title"
            data-testid="confirmation-modal-title"
          >
            {title}
          </div>
        )}
        {message && (
          <div
            className="confirmation-modal__message"
            data-testid="confirmation-modal-message"
          >
            {message}
          </div>
        )}
        <div
          className="confirmation-modal__actions"
          data-testid="confirmation-modal-actions"
        >
          {onCancel && (
            <Button
              variant="secondary"
              onClick={onCancel}
              data-testid="confirmation-modal-cancel"
            >
              {cancelLabel ?? dictionary.cancel}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={onConfirm}
            data-testid="confirmation-modal-confirm"
          >
            {confirmLabel ?? dictionary.confirm}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
